import {
  AppBar,
  Box,
  CardMedia,
  CircularProgress,
  Container,
  Drawer,
  IconButton,
  Slider,
  Toolbar,
  Typography,
} from "@mui/material";
import { useContext } from "react";

import {
  ExpandMore,
  PauseCircle,
  PlayCircle,
  Repeat,
  Shuffle,
  SkipNext,
  SkipPrevious,
} from "@mui/icons-material";

import ActionDrawer from "@/components/ActionDrawer";
import CommentsContainer from "@/components/Comments/CommentsContainer";
import { MusicContext } from "@/contexts/MusicContext";
import CaptionContainer from "@/fragments/CaptionContainer";
import { formatSeconds, getContentArtwork } from "@/utils";
import AppLogo from "@/fragments/AppLogo";
import LyricsContainer from "@/components/LyricsContainer";

export default function FullSizePlayer({ compact, setCompact }) {
  const { currentSong } = useContext(MusicContext);

  return (
    <Drawer
      anchor="bottom"
      open={!compact}
      onClose={() => setCompact(!compact)}
      PaperProps={{ style: { background: "#47596E" } }}
    >
      <AppBar position="sticky" elevation={0} style={{ background: "#47596E" }}>
        <Toolbar style={{ display: "flex", justifyContent: "space-between" }}>
          <IconButton onClick={() => setCompact(!compact)}>
            <ExpandMore style={{ fontSize: "30px" }} />
          </IconButton>

          <AppLogo />

          <ActionDrawer content={currentSong} />
        </Toolbar>
      </AppBar>
      <Container maxWidth="sm">
        <Box
          style={{
            display: "flex",
            flexDirection: "column",
          }}
        >
          <CardMedia
            component="img"
            sx={{
              borderRadius: "10px",
              width: "100%",
              aspectRatio: "1/1",
              my: 1,
            }}
            src={getContentArtwork(currentSong)}
          />

          {/* Assuming PlayerControls is imported elsewhere */}
          <PlayerControls />
        </Box>

        <LyricsContainer currentSong={currentSong} />
        <CreditsContainer currentSong={currentSong} />

        <Box sx={{ mt: 2, pt: 2, mb: 10 }}>
          <CaptionContainer content={currentSong} />
          <CommentsContainer content={currentSong} />
        </Box>
      </Container>
    </Drawer>
  );
}

function CreditsContainer({ currentSong }) {
  const producers = currentSong.meta.producers;
  const songwriters = currentSong.meta.writers;

  return (
    <Box sx={{ my: 4 }}>
      {songwriters?.length > 0 && (
        <>
          <Typography variant="h6" color="inherit">
            Written by
          </Typography>
          {songwriters}
        </>
      )}
      {producers?.length > 0 && (
        <>
          <Typography variant="h6" color="inherit" sx={{ mt: 2 }}>
            Produced by
          </Typography>
          {producers}
        </>
      )}
    </Box>
  );
}

function PlayerControls() {
  const {
    currentSong,
    isPlaying,
    isAudioLoading,
    handlePlay,
    handlePause,
    handleNext,
    handlePrevious,
    duration,
    currentTime,
    handleTimeSliderChange,
    repeatMode,
    toggleRepeatMode,
    shuffleMode,
    toggleShuffleMode,
  } = useContext(MusicContext);

  const filteredTags = currentSong.meta.tags
    ? currentSong.meta.tags.filter((tag) => tag.trim() !== "")
    : [];

  return (
    <Box id="player-controls" sx={{ width: "100%", my: 2 }}>
      <Typography variant="h6" color="inherit">
        {currentSong?.meta["title"]}
      </Typography>
      <Typography
        style={{ marginTop: "0px", fontSize: "0.8em", color: "lightgray" }}
      >
        {currentSong?.owner.name}
        {filteredTags.length > 0 && `, ${filteredTags.join(", ")}`}
      </Typography>

      <Slider
        style={{ marginTop: "10px", paddingBottom: "0px" }}
        value={currentTime}
        max={duration}
        onChange={handleTimeSliderChange}
        color="secondary"
      />
      <Box
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "-10px",
          marginBottom: "0",
        }}
      >
        <Typography variant="caption" color="lightgray">
          {formatSeconds(currentTime)}{" "}
        </Typography>
        <Typography variant="caption" color="lightgray">
          {formatSeconds(duration - currentTime)}
        </Typography>
      </Box>

      <Box
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <IconButton onClick={toggleShuffleMode}>
          {shuffleMode ? (
            <Shuffle style={{ fontSize: "30px" }} color="secondary" />
          ) : (
            <Shuffle style={{ fontSize: "30px", opacity: 0.7 }} />
          )}
        </IconButton>
        <IconButton onClick={handlePrevious}>
          <SkipPrevious style={{ fontSize: "50px" }} />
        </IconButton>

        {isAudioLoading ? (
          <IconButton disabled>
            <CircularProgress size={70} color="inherit" />
          </IconButton>
        ) : isPlaying ? (
          <IconButton onClick={handlePause}>
            <PauseCircle style={{ fontSize: "70px" }} />
          </IconButton>
        ) : (
          <IconButton onClick={() => handlePlay(currentSong)}>
            <PlayCircle style={{ fontSize: "70px" }} />
          </IconButton>
        )}

        <IconButton onClick={handleNext}>
          <SkipNext style={{ fontSize: "50px" }} />
        </IconButton>
        <IconButton onClick={toggleRepeatMode}>
          {repeatMode ? (
            <Repeat style={{ fontSize: "30px" }} color="secondary" />
          ) : (
            <Repeat style={{ fontSize: "30px", opacity: 0.7 }} />
          )}
        </IconButton>
      </Box>
    </Box>
  );
}
